'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import MultiSelect from '@/components/ui/multi-select';
import { getAuthHeaders } from '@/lib/authHeaders';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import Select, { type StylesConfig, type MultiValue } from 'react-select';
import ContactPicker from '@/components/ContactPicker';
import LocationPicker from '@/components/LocationPicker';
import LocationSearchPicker from '@/components/LocationSearchPicker';
import UserMultiSelect from '@/components/UserMultiSelect';
import ProductImagesField, { type ProductImage } from './ProductImagesField';
import { MeasurementField } from './fields/MeasurementField';
import { PassFailField } from './fields/PassFailField';
import { PhotoCaptureField } from './fields/PhotoCaptureField';
import { SignaturePadField } from './fields/SignaturePadField';
import { GpsStampField } from './fields/GpsStampField';
import { BarcodeScannerField } from './fields/BarcodeScannerField';
import { CommentsField } from './fields/CommentsField';
import { OtpVerificationField } from './fields';
import { RatingField } from './fields/RatingField';
import { LinearScaleField } from './fields/LinearScaleField';
import { MatrixGridField } from './fields/MatrixGridField';
import { RankingField } from './fields/RankingField';
import { DividerField } from './fields/DividerField';
import { HeadingField } from './fields/HeadingField';
import { ImageDisplayField } from './fields/ImageDisplayField';
import { compressImageFile } from '@/lib/compressImage';
import { getCanonicalFieldType } from '@/lib/forms/fieldTypes';
import { validateFieldValue } from './engine/fieldValidation';
import { asInputValue, asArray, asStringArray, type FieldValue, type FormDataValues } from './formFieldTypes';

/** workItemId props accept string|number ids — coerce the form-data bag value. */
const workItemIdString = (v: FieldValue): string | undefined =>
  v === null || v === undefined || typeof v === 'object' ? undefined : String(v);

// Tags Input Field Component
interface TagsInputFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: string[]) => void;
  isDark: boolean;
}

const TagsInputField: React.FC<TagsInputFieldProps> = ({ field, value, onChange, isDark }) => {
  const [tagInput, setTagInput] = useState('');
  const tags = asStringArray(value);
  
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!tags.includes(newTag)) {
        onChange([...tags, newTag]);
      }
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };
  
  const getInputClasses = () => `
    block w-full rounded-md px-3 py-2 text-sm border
    focus:outline-none focus:ring-2 focus:ring-teal-500
    ${isDark 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
    }
  `;
  
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag, index) => (
          <span
            key={index}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
              isDark
                ? 'bg-blue-900 text-blue-200 border border-blue-700'
                : 'bg-blue-100 text-blue-800 border border-blue-300'
            }`}
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className={`ml-1 rounded-full p-0.5 ${
                isDark ? 'hover:bg-blue-200/20' : 'hover:bg-blue-800/20'
              }`}
              aria-label={`Remove ${tag}`}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        onKeyDown={handleAddTag}
        placeholder={field.placeholder || 'Type and press Enter to add'}
        className={getInputClasses()}
      />
      {field.help_text && (
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {field.help_text}
        </p>
      )}
    </div>
  );
};

// File Upload Field Component
interface FileUploadFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({ field, value, onChange, isDark }) => {
  const isRequired = !!(field.required || field.is_required);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{file_url: string; file_name: string; file_size?: number} | null>(
    typeof value === 'string' && value
      ? { file_url: value, file_name: 'Uploaded file' }
      : value && typeof value === 'object' && !Array.isArray(value) && typeof value.file_url === 'string'
        ? (value as { file_url: string; file_name: string; file_size?: number })
        : null
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (metadata.maxSize is in bytes)
    const maxSize = field.metadata?.maxSize || 5242880; // Default 5MB
    if (file.size > maxSize) {
      setError(`File size exceeds ${formatFileSize(maxSize)}`);
      return;
    }

    setError(null);
    setUploading(true);
    setProgress(0);

    try {
      // Upload to static file server via our API
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', field.key);

      const authHeaders = getAuthHeaders();
      const response = await fetch('/api/v1/my-onboarding/upload', {
        method: 'POST',
        headers: {
          ...authHeaders,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      
      // Store the uploaded file info
      const fileInfo = {
        file_url: data.document.file_url,
        file_name: data.document.file_name,
        file_size: data.document.file_size,
        document_id: data.document.id,
      };
      
      setUploadedFile(fileInfo);
      onChange(fileInfo.file_url); // Store the file URL
      setProgress(100);

    } catch (err: unknown) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getInputClasses = () => `
    block w-full rounded-md px-3 py-2 text-sm
    focus:outline-none focus:ring-2 focus:ring-teal-500
    ${isDark 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
    }
    ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
    ${error ? 'border-red-500' : 'border'}
  `;

  return (
    <div className="space-y-2">
      <input
        type="file"
        onChange={handleFileChange}
        accept={field.metadata?.accept || '*'}
        className={getInputClasses()}
        required={isRequired && !uploadedFile}
        disabled={uploading}
      />
      
      {field.metadata?.maxSizeLabel && (
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Maximum file size: {field.metadata.maxSizeLabel}
        </p>
      )}

      {uploading && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Uploading...</span>
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-teal-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {uploadedFile && !error && (
        <div className={`flex items-center justify-between p-3 rounded-md ${
          isDark ? 'bg-gray-700' : 'bg-green-50'
        }`}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {uploadedFile.file_name}
              </p>
              {uploadedFile.file_size && (
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {formatFileSize(uploadedFile.file_size)}
                </p>
              )}
            </div>
          </div>
          <a
            href={uploadedFile.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            View
          </a>
        </div>
      )}
    </div>
  );
};

// Image Upload Field Component (specifically for logo/hero images)
interface ImageUploadFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({ field, value, onChange, isDark }) => {
  const isRequired = !!(field.required || field.is_required);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(typeof value === 'string' && value ? value : null);

  // Helper function to check if this is a logo or hero field
  const isLogoOrHeroField = () => {
    return field.key === 'logo_url' || 
           field.key.startsWith('hero') ||
           field.label?.toLowerCase().includes('logo') ||
           field.label?.toLowerCase().includes('hero');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Check file size (max 2MB for images)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setError('Image size must be less than 2MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const compressedFile = await compressImageFile(file, 1920, 0.7);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);

      // Upload to static file server
      const formData = new FormData();
      formData.append('file', compressedFile);

      const apiKey = process.env.NEXT_PUBLIC_STATIC_FILE_SERVER_API_KEY || 'a11abd7b-0d47-4fe2-a66f-f7c405cf5f80';
      
      const response = await fetch('https://staticfileserver.totalaccess.co.za/upload', {
        method: 'POST',
        headers: {
          'x-api-key': apiKey
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      if (result.success && result.directUrl) {
        setPreviewUrl(result.directUrl);
        onChange(result.directUrl);
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (err: unknown) {
      console.error('Image upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setPreviewUrl(typeof value === 'string' ? value : null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onChange('');
  };

  const getFieldName = () => {
    if (field.key === 'logo_url') return 'Logo';
    if (field.key === 'hero1_url') return 'Hero Image 1';
    if (field.key === 'hero2_url') return 'Hero Image 2';
    if (field.key === 'hero3_url') return 'Hero Image 3';
    return field.label;
  };

  const getRecommendedSize = () => {
    if (field.key === 'logo_url') return '120x120px';
    if (field.key.startsWith('hero')) return '1920x400px';
    return '';
  };

  return (
    <div className="space-y-2">
      <label className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
        {getFieldName()}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {/* Image Preview */}
      {previewUrl && (
        <div className="relative rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <img
            src={previewUrl}
            alt={getFieldName()}
            className="w-full h-40 object-contain"
            onError={() => setPreviewUrl(null)}
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
            title="Remove image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center space-x-2">
        <label 
          className={`
            inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 
            rounded-md shadow-sm text-sm font-medium 
            ${uploading 
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed' 
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
            }
            focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500
          `}
        >
          {uploading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </>
          ) : (
            <>
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {previewUrl ? 'Change Image' : 'Upload Image'}
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="sr-only"
          />
        </label>

        {/* Manual URL Input - Hidden for logo/hero fields */}
        {!isLogoOrHeroField() && (
          <input
            type="url"
            value={asInputValue(value)}
            onChange={(e) => {
              onChange(e.target.value);
              setPreviewUrl(e.target.value);
            }}
            placeholder={field.placeholder || 'Or paste image URL'}
            className={`
              flex-1 rounded-md px-3 py-2 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500
              ${isDark 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }
              border
            `}
            disabled={uploading}
          />
        )}
      </div>

      {/* Help Text */}
      <div className="flex items-start space-x-2">
        {getRecommendedSize() && (
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Recommended: {getRecommendedSize()} • Max 2MB • JPG, PNG, WebP
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-1 text-red-600 text-sm">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

interface FormFieldOption {
  value: string;
  label: string;
  description?: string;
  /** Extra keys carried through from endpoint payloads (e.g. start_time/end_time). */
  [key: string]: unknown;
}

interface FormFieldValidation {
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
}

interface FormFieldStyling {
  classes?: string;
  containerClasses?: string;
  /** Background colour for the field container */
  background_color?: string;
  /** Text colour for the field label/value */
  text_color?: string;
  /** Border colour for the field container */
  border_color?: string;
  /** Border radius in pixels */
  border_radius?: number | string;
  /** Font size (px or rem) */
  font_size?: number | string;
  /** Font family override */
  font_family?: string;
  /** Padding (px or CSS value) */
  padding?: number | string;
  /** Margin (px or CSS value) */
  margin?: number | string;
  /** Raw CSS appended to the container style */
  custom_css?: string;
}

function buildFieldStyles(styling?: FormFieldStyling | null): {
  container?: React.CSSProperties;
  label?: React.CSSProperties;
} {
  if (!styling) return {};
  const container: React.CSSProperties = {};
  const label: React.CSSProperties = {};
  if (styling.background_color) container.backgroundColor = styling.background_color;
  if (styling.text_color) {
    container.color = styling.text_color;
    label.color = styling.text_color;
  }
  if (styling.border_color) container.borderColor = styling.border_color;
  if (styling.border_radius != null) {
    container.borderRadius = typeof styling.border_radius === 'number'
      ? `${styling.border_radius}px`
      : styling.border_radius;
  }
  if (styling.font_size != null) {
    container.fontSize = typeof styling.font_size === 'number' ? `${styling.font_size}px` : styling.font_size;
    label.fontSize = container.fontSize;
  }
  if (styling.font_family) {
    container.fontFamily = styling.font_family;
    label.fontFamily = styling.font_family;
  }
  if (styling.padding != null) {
    container.padding = typeof styling.padding === 'number' ? `${styling.padding}px` : styling.padding;
  }
  if (styling.margin != null) {
    container.margin = typeof styling.margin === 'number' ? `${styling.margin}px` : styling.margin;
  }
  if (styling.custom_css) {
    styling.custom_css
      .split(';')
      .filter(Boolean)
      .forEach((pair) => {
        const [rawKey, ...valueParts] = pair.split(':');
        const key = rawKey?.trim();
        const value = valueParts.join(':').trim();
        if (key && value) {
          const camelKey = key.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
          (container as Record<string, string | number>)[camelKey] = value;
        }
      });
  }
  return {
    container: Object.keys(container).length > 0 ? container : undefined,
    label: Object.keys(label).length > 0 ? label : undefined,
  };
}

/** Sub-field rendered inside module-checkbox-group / module-role-config values. */
interface ConditionalSubField {
  key: string;
  type?: string;
  label?: string;
  required?: boolean;
  is_required?: boolean;
  placeholder?: string;
  description?: string;
  options?: FormFieldOption[];
  endpoint?: string;
  responseField?: string;
  labelField?: string;
  valueField?: string;
  default_value?: unknown;
  defaultValue?: unknown;
  metadata?: FormFieldMetadata;
  [key: string]: unknown;
}

/** Field `metadata` bag — declared keys cover every key read in this file. */
interface FormFieldMetadata {
  accept?: string;
  maxSize?: number;
  maxSizeLabel?: string;
  module_key?: string;
  source?: string;
  filter?: string;
  exclude?: string[];
  include?: string[];
  showDetails?: boolean;
  // module-checkbox-group / module-role-config
  autoGrantModules?: string[];
  autoGrantMessage?: string;
  showRoleSelector?: boolean;
  allowedRoles?: string[];
  defaultRole?: string;
  conditionalFields?: Record<string, ConditionalSubField[]>;
  modules?: ModuleRoleModule[];
  // picker callbacks (runtime-only, not persisted)
  updateFields?: (data: unknown) => void;
  autoPopulateContact?: boolean;
  onLocationSelected?: (location: unknown) => void;
  // product_images
  workItemId?: number | string;
  maxImages?: number;
  maxSizeMB?: number;
  showPrimarySelector?: boolean;
  showCaptions?: boolean;
  showAltText?: boolean;
  acceptedTypes?: string;
  [key: string]: unknown;
}

/** Module descriptor inside module-role-config metadata.modules. */
interface ModuleRoleModule {
  key: string;
  label?: string;
  description?: string;
  requiredFields?: ConditionalSubField[];
  [key: string]: unknown;
}

/** Element of a module-role / module-checkbox value array. */
interface ModuleRoleAssignment {
  module_key: string;
  role?: string;
  [key: string]: unknown;
}

/** Geofence assignment element stored in a geofence_assignment_manager value. */
interface GeofenceAssignment {
  id?: number;
  geofence_id: number;
  geofence_name?: string;
  geofence_description?: string;
  geofence_type?: string;
  allowed_platform?: string;
  allow_offline?: boolean;
  allow_supervisor_override?: boolean;
  allow_enrollment_recapture?: boolean;
  max_offline?: number | null;
  [key: string]: unknown;
}

/** Geofence row from /api/v1/time-attendance/geofences. */
interface GeofenceRecord {
  id: number;
  name: string;
  description?: string;
  geofence_type?: string;
  [key: string]: unknown;
}

interface GeofenceSelectOption {
  value: number;
  label: string;
  type?: string;
  description?: string;
}

interface FormField {
  key: string;
  type: string;
  label: string;
  required?: boolean;
  is_required?: boolean;
  placeholder?: string;
  options?: FormFieldOption[] | {
    module_key?: string;
    depends_on_contact?: boolean;
    options?: FormFieldOption[];
    choices?: FormFieldOption[];
    items?: FormFieldOption[];
    [key: string]: unknown;
  }; // Array for select, object for user_multi_select
  validation?: FormFieldValidation;
  sort_order: number;
  width?: string;
  styling?: FormFieldStyling;
  currency?: string; // For currency field type (e.g., 'ZAR', 'USD', 'EUR')
  description?: string; // Field description/help text
  help_text?: string; // Help text displayed below the field
  helpText?: string; // Alternative help text property (used by info field type)
  endpoint?: string; // API endpoint to fetch options from
  responseField?: string; // Field in API response containing options array or value
  labelField?: string; // Field name for option label
  valueField?: string; // Field name for option value
  searchable?: boolean; // Whether field is searchable
  showDescriptions?: boolean; // Whether to show option descriptions
  readonly?: boolean; // Whether field is read-only
  is_readonly?: boolean; // Builder-side read-only flag (alias for readonly)
  autoFetch?: boolean; // Auto-fetch value from endpoint on mount (for text/readonly fields)
  componentType?: string; // For custom field types - specifies which component to render (e.g., 'geofence_assignment_manager')
  data_source?: string; // Dynamic data source (e.g., 'users_with_module_access', 'all_users', 'contacts')
  module_key?: string; // Module key for filtering users (used with data_source or user_multi_select)
  default_value?: unknown;
  defaultValue?: unknown;
  metadata?: FormFieldMetadata;
  /** Schema-defined fields carry additional dynamic keys. */
  [key: string]: unknown;
}

// Geofence Assignment Manager Component
interface GeofenceAssignmentManagerProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  isDark: boolean;
  readonly?: boolean;
}

const GeofenceAssignmentManager: React.FC<GeofenceAssignmentManagerProps> = ({
  field,
  value,
  onChange,
  isDark,
  readonly = false
}) => {
  const [availableGeofences, setAvailableGeofences] = useState<GeofenceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGeofences, setSelectedGeofences] = useState<number[]>([]);
  const [platform, setPlatform] = useState<string>('any');
  const [settings, setSettings] = useState({
    allow_offline: true,
    allow_supervisor_override: false,
    allow_enrollment_recapture: true,
    max_offline: null as number | null
  });

  const assignments = useMemo(() => asArray<GeofenceAssignment>(value), [value]);

  const fetchGeofences = async () => {
    try {
      setLoading(true);
      const authHeaders = getAuthHeaders();
      const response = await fetch('/api/v1/time-attendance/geofences?active=true', {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableGeofences(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching geofences:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeofences();
  }, []);

  useEffect(() => {
    // Initialize selected geofences from current assignments
    if (assignments.length > 0) {
      const geofenceIds = assignments.map((a) => a.geofence_id);
      setSelectedGeofences(geofenceIds);
      
      // Use settings from first assignment (if all share same settings, they'll be consistent)
      if (assignments[0]) {
        setSettings({
          allow_offline: assignments[0].allow_offline ?? true,
          allow_supervisor_override: assignments[0].allow_supervisor_override ?? false,
          allow_enrollment_recapture: assignments[0].allow_enrollment_recapture ?? true,
          max_offline: assignments[0].max_offline ?? null
        });
        setPlatform(assignments[0].allowed_platform || 'any');
      }
    }
  }, [value, assignments]);

  const handleGeofenceChange = (selectedOptions: MultiValue<GeofenceSelectOption>) => {
    const ids = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    setSelectedGeofences(ids);
    
    // Update the assignments array
    const newAssignments = ids.map((geofenceId: number) => {
      const geofence = availableGeofences.find(g => g.id === geofenceId);
      const existingAssignment = assignments.find((a) => a.geofence_id === geofenceId);
      
      return {
        geofence_id: geofenceId,
        geofence_name: geofence?.name,
        geofence_description: geofence?.description,
        geofence_type: geofence?.geofence_type,
        allowed_platform: platform,
        ...settings,
        // Preserve existing assignment id if it exists
        id: existingAssignment?.id
      };
    });
    
    onChange(newAssignments);
  };

  const updateAllAssignments = (updates: Partial<GeofenceAssignment>) => {
    const updatedAssignments = assignments.map((assignment) => ({
      ...assignment,
      ...updates
    }));
    onChange(updatedAssignments);
  };

  const handlePlatformChange = (newPlatform: string) => {
    setPlatform(newPlatform);
    updateAllAssignments({ allowed_platform: newPlatform });
  };

  const handleSettingChange = (setting: keyof typeof settings, value: boolean | number | null) => {
    const newSettings = { ...settings, [setting]: value };
    setSettings(newSettings);
    updateAllAssignments(newSettings);
  };

  if (readonly) {
    // Read-only view (original display code)
    return (
      <div
        className={`space-y-4 rounded-lg border p-4 ${
          isDark ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'
        } ${field.styling?.containerClasses || ''}`}
      >
        {assignments.length > 0 ? (
          <div className="space-y-2">
            <div className="text-sm font-medium mb-3">Active Geofence Assignments</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                    <th className="text-left py-2 px-3">Geofence</th>
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-left py-2 px-3">Platform</th>
                    <th className="text-left py-2 px-3">Offline</th>
                    <th className="text-left py-2 px-3">Settings</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment, index: number) => (
                    <tr key={index} className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium">{assignment.geofence_name || `Geofence ${assignment.geofence_id}`}</div>
                        {assignment.geofence_description && (
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {assignment.geofence_description}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          assignment.geofence_type === 'GPS' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200' :
                          assignment.geofence_type === 'WIFI' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200' :
                          assignment.geofence_type === 'Beacon' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {assignment.geofence_type || 'GPS'}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                          isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {assignment.allowed_platform === 'any' ? 'All Platforms' : 
                           assignment.allowed_platform === 'mobile' ? 'Mobile' :
                           assignment.allowed_platform === 'portal' ? 'Portal' :
                           assignment.allowed_platform}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {assignment.allow_offline ? (
                          <span className="text-green-600 text-xs">✓ Allowed</span>
                        ) : (
                          <span className="text-red-600 text-xs">✗ Disabled</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-xs space-y-1">
                          {assignment.allow_supervisor_override && (
                            <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              ✓ Supervisor override
                            </div>
                          )}
                          {assignment.allow_enrollment_recapture && (
                            <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              ✓ Re-capture allowed
                            </div>
                          )}
                          {assignment.max_offline && (
                            <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              Max offline: {assignment.max_offline}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div className="text-sm font-medium mb-1">No geofence assignments</div>
            <div className="text-xs">This employee has not been assigned to any geofences yet</div>
          </div>
        )}
        
        {field.helpText && (
          <div className={`text-xs mt-3 pt-3 border-t ${
            isDark ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-600'
          }`}>
            {field.helpText}
          </div>
        )}
      </div>
    );
  }

  // Editable view
  const geofenceOptions = availableGeofences.map(g => ({
    value: g.id,
    label: g.name,
    type: g.geofence_type,
    description: g.description
  }));

  const selectedOptions = geofenceOptions.filter(opt => 
    selectedGeofences.includes(opt.value)
  );

  const customStyles: StylesConfig<GeofenceSelectOption, true> = {
    control: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      borderColor: isDark ? '#4b5563' : '#d1d5db',
      color: isDark ? '#ffffff' : '#000000',
      '&:hover': {
        borderColor: isDark ? '#6b7280' : '#9ca3af'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (isDark ? '#4b5563' : '#f3f4f6')
        : (isDark ? '#374151' : '#ffffff'),
      color: isDark ? '#ffffff' : '#000000',
      '&:hover': {
        backgroundColor: isDark ? '#4b5563' : '#f3f4f6'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? '#4b5563' : '#e5e7eb'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? '#ffffff' : '#000000'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? '#9ca3af' : '#6b7280',
      '&:hover': {
        backgroundColor: isDark ? '#6b7280' : '#d1d5db',
        color: isDark ? '#ffffff' : '#000000'
      }
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#ffffff' : '#000000'
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#ffffff' : '#000000'
    })
  };

  return (
    <div
      className={`space-y-4 rounded-lg border p-4 ${
        isDark ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'
      } ${field.styling?.containerClasses || ''}`}
    >
      {/* Geofence Selection */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          Select Geofences
        </label>
        {loading ? (
          <div className="text-sm text-gray-500">Loading geofences...</div>
        ) : (
          <Select
            isMulti
            options={geofenceOptions}
            value={selectedOptions}
            onChange={handleGeofenceChange}
            styles={customStyles}
            placeholder="Select geofences..."
            className="text-sm"
            formatOptionLabel={(option) => (
              <div>
                <div className="font-medium">{option.label}</div>
                {option.description && (
                  <div className="text-xs text-gray-500">{option.description}</div>
                )}
              </div>
            )}
          />
        )}
      </div>

      {/* Platform Selection */}
      {selectedGeofences.length > 0 && (
        <>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Allowed Platform
            </label>
            <select
              value={platform}
              onChange={(e) => handlePlatformChange(e.target.value)}
              className={`block w-full rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="any">All Platforms</option>
              <option value="mobile">Mobile Only</option>
              <option value="portal">Portal Only</option>
              <option value="software_monitor">Software Monitor</option>
              <option value="ta_terminal">TA Terminal</option>
            </select>
          </div>

          {/* Settings */}
          <div className="space-y-3">
            <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Settings
            </div>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.allow_offline}
                onChange={(e) => handleSettingChange('allow_offline', e.target.checked)}
                className="rounded text-teal-600 focus:ring-teal-500"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Allow offline clock in/out
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.allow_supervisor_override}
                onChange={(e) => handleSettingChange('allow_supervisor_override', e.target.checked)}
                className="rounded text-teal-600 focus:ring-teal-500"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Allow supervisor override
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.allow_enrollment_recapture}
                onChange={(e) => handleSettingChange('allow_enrollment_recapture', e.target.checked)}
                className="rounded text-teal-600 focus:ring-teal-500"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Allow enrollment re-capture
              </span>
            </label>

            {settings.allow_offline && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Max offline events
                </label>
                <input
                  type="number"
                  value={settings.max_offline || ''}
                  onChange={(e) => handleSettingChange('max_offline', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Unlimited"
                  className={`block w-full rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            )}
          </div>

          {/* Current Assignments Preview */}
          <div className={`pt-3 border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="text-xs font-medium mb-2">Selected Geofences ({selectedGeofences.length})</div>
            <div className="flex flex-wrap gap-2">
              {selectedOptions.map((opt) => (
                <span key={opt.value} className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                  isDark ? 'bg-teal-900/30 text-teal-200' : 'bg-teal-100 text-teal-700'
                }`}>
                  {opt.label}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {field.helpText && (
        <div className={`text-xs pt-3 border-t ${
          isDark ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-600'
        }`}>
          {field.helpText}
        </div>
      )}
    </div>
  );
};

interface DynamicFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  enableImageUpload?: boolean;
  fullFormData?: FormDataValues; // Full form data for field dependencies
  /** When true, renders the field value as non-interactive static text */
  readOnly?: boolean;
  /** Optional step/field number chip displayed before the label */
  fieldNumber?: number;
}

// Dynamic Conditional Field Component
// Used for rendering conditional fields within module-checkbox-group
const DynamicConditionalField: React.FC<{
  field: ConditionalSubField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}> = ({ field, value, onChange, isDark }) => {
  const isRequired = !!(field.required || field.is_required);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [options, setOptions] = useState<FormFieldOption[]>([]);

  useEffect(() => {
    // If field has static options defined, use those
    if (field.options && Array.isArray(field.options)) {
      setOptions(field.options);
      return;
    }
    
    // Otherwise, load from endpoint if specified
    if (field.endpoint && field.type === 'select') {
      const endpoint = field.endpoint;
      const loadOptions = async () => {
        try {
          setLoadingOptions(true);
          const headers = getAuthHeaders();
          const response = await fetch(endpoint, { headers });
          
          if (!response.ok) {
            throw new Error(`Failed to load options: ${response.statusText}`);
          }
          
          const data: unknown = await response.json();
          let optionsArray: unknown =
            field.responseField && data && typeof data === 'object'
              ? (data as Record<string, unknown>)[field.responseField]
              : data;

          if (!Array.isArray(optionsArray)) {
            optionsArray = [];
          }

          const mappedOptions: FormFieldOption[] = (optionsArray as Record<string, unknown>[]).map((item) => ({
            value: String(field.valueField ? item[field.valueField] : item.value ?? ''),
            label: String(field.labelField ? item[field.labelField] : item.label ?? ''),
            ...item // Include all fields for metadata display
          }));
          
          setOptions(mappedOptions);
        } catch (error) {
          console.error('[DynamicConditionalField] Error loading options:', error);
        } finally {
          setLoadingOptions(false);
        }
      };
      
      loadOptions();
    }
  }, [field.endpoint, field.type, field.responseField, field.labelField, field.valueField, field.options]);

  const getInputClasses = () => {
    return `w-full px-2 py-1.5 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 transition-colors ${
      isDark 
        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400' 
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
    }`;
  };

  return (
    <div className="space-y-1">
      <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        {field.label}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {field.description && (
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {field.description}
        </p>
      )}
      
      {field.type === 'select' ? (
        loadingOptions ? (
          <div className={`${getInputClasses()} flex items-center justify-center py-2`}>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-xs">Loading...</span>
          </div>
        ) : (
          <select
            value={asInputValue((value ?? field.default_value ?? field.defaultValue ?? '') as FieldValue)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {options.map((option, index) => (
              <option key={`option-${index}-${option.value}`} value={option.value}>
                {option.label}
                {field.metadata?.showDetails && option.start_time && option.end_time
                  ? ` (${String(option.start_time)} - ${String(option.end_time)})`
                  : null
                }
              </option>
            ))}
          </select>
        )
      ) : (
        <input
          type={field.type || 'text'}
          value={asInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={getInputClasses()}
        />
      )}
    </div>
  );
};

export const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  value,
  onChange,
  enableImageUpload = false,
  fullFormData,
  readOnly = false,
  fieldNumber,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  // Normalize required / read-only — builder uses is_*, runtime uses flat names
  const isRequired = !!(field.required || field.is_required);
  const fieldReadOnly = !!(field.readonly || field.is_readonly);
  const isFieldReadOnly = readOnly || fieldReadOnly;

  // Inline validation (pattern, minLength/maxLength, numeric min/max)
  const validationError = useMemo(() => {
    const result = validateFieldValue(value, field.validation);
    return result.valid ? null : (result.message ?? null);
  }, [value, field.validation]);

  // Richer per-field styling (colours, radius, fonts, custom CSS)
  const fieldStyles = useMemo(() => buildFieldStyles(field.styling), [field.styling]);

  // State for dynamically loaded options
  const [loadedOptions, setLoadedOptions] = useState<FormFieldOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  
  // Auto-fetch value from endpoint for readonly text fields
  useEffect(() => {
    if (field.autoFetch && field.endpoint && !value && (field.type === 'text' || isFieldReadOnly)) {
      const fetchValue = async () => {
        try {
          const authHeaders = getAuthHeaders();
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...authHeaders
          };
          
          const response = await fetch(field.endpoint!, { headers });
          if (!response.ok) {
            throw new Error(`Failed to fetch value: ${response.statusText}`);
          }
          
          const data = await response.json();
          
          // Extract value from response
          let fetchedValue = data;
          if (field.responseField) {
            fetchedValue = data[field.responseField];
          }
          
          // Set the value
          if (fetchedValue) {
            onChange(fetchedValue);
          }
        } catch (error) {
          console.error('[DynamicField] Error auto-fetching value:', error);
        }
      };
      
      fetchValue();
    }
  }, [field.autoFetch, field.endpoint, field.type, isFieldReadOnly, field.responseField, onChange, value]);
  
  // Load options from endpoint if provided OR from dynamic sources
  useEffect(() => {
    // Normalize field type for option-loading checks (canonical + legacy names)
    const ft = getCanonicalFieldType(field.type);
    const isMultiSelect = ft === 'multi_select';
    const isDynamicSelect = ft === 'dynamic_select';
    const needsOptions = field.endpoint && (ft === 'select' || isMultiSelect || isDynamicSelect || field.type === 'module-checkbox-group' || field.type === 'module-role-config');
    const needsDynamicOptions = isDynamicSelect && field.metadata?.source === 'client_departments';
    const needsModuleOptions = field.type === 'module-checkbox-group' || field.type === 'module-role-config';
    const needsDataSourceOptions = field.data_source && (ft === 'select' || isMultiSelect);
    
    if (needsOptions || needsDynamicOptions || needsModuleOptions || needsDataSourceOptions) {
      const loadOptions = async () => {
        setLoadingOptions(true);
        setOptionsError(null);
        
        try {
          // Get authentication headers using authHeaders utility
          const authHeaders = getAuthHeaders();
          
          const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...authHeaders
          };
          
          let response: Response;
          let data: unknown;
          
          // Handle different option sources
          if (field.data_source) {
            // Handle data_source attribute (e.g., users_with_module_access)
            if (field.data_source === 'users_with_module_access' && field.module_key) {
              response = await fetch(`/api/v1/module-access/users?module_key=${field.module_key}`, { headers });
              if (!response.ok) {
                throw new Error(`Failed to load users: ${response.statusText}`);
              }
              data = await response.json();

              const users =
                (data as { success?: boolean; data?: Array<{ id: string | number; first_name?: string; last_name?: string; email?: string }> })
                  .success
                  ? ((data as { data?: Array<{ id: string | number; first_name?: string; last_name?: string; email?: string }> }).data ?? [])
                  : [];
              const mappedOptions: FormFieldOption[] = users.map((user) => ({
                value: String(user.id),
                label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || ''
              }));
              
              setLoadedOptions(mappedOptions);
              
            } else if (field.data_source === 'all_users') {
              response = await fetch('/api/v1/users', { headers });
              if (!response.ok) {
                throw new Error(`Failed to load users: ${response.statusText}`);
              }
              data = await response.json();

              const userRows: Array<{ id: string | number; first_name?: string; last_name?: string; email?: string }> = Array.isArray(data)
                ? data
                : (((data as { users?: unknown[]; data?: unknown[] }).users
                    ?? (data as { data?: unknown[] }).data
                    ?? []) as Array<{ id: string | number; first_name?: string; last_name?: string; email?: string }>);
              const mappedOptions: FormFieldOption[] = userRows.map((user) => ({
                value: String(user.id),
                label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || ''
              }));
              
              setLoadedOptions(mappedOptions);
              
            } else if (field.data_source === 'contacts') {
              response = await fetch('/api/v1/finance/contacts', { headers });
              if (!response.ok) {
                throw new Error(`Failed to load contacts: ${response.statusText}`);
              }
              data = await response.json();

              const contacts =
                (data as { success?: boolean; data?: Array<{ id: string | number; name?: string; company_name?: string }> }).success
                  ? ((data as { data?: Array<{ id: string | number; name?: string; company_name?: string }> }).data ?? [])
                  : [];
              const mappedOptions: FormFieldOption[] = contacts.map((contact) => ({
                value: String(contact.id),
                label: contact.name || contact.company_name || ''
              }));
              
              setLoadedOptions(mappedOptions);
            }
            
          } else if (isDynamicSelect && field.metadata?.source === 'client_departments') {
            // Load departments from client_details
            response = await fetch('/api/v1/client/departments', { headers });
            if (!response.ok) {
              throw new Error(`Failed to load departments: ${response.statusText}`);
            }
            data = await response.json();
            
            // Handle both array response and object with data property
            const departments: string[] = Array.isArray(data)
              ? (data as string[])
              : ((((data as { departments?: unknown[]; data?: unknown[] }).departments
                  ?? (data as { data?: unknown[] }).data
                  ?? []) as unknown[]).filter((d): d is string => typeof d === 'string'));

            const mappedOptions: FormFieldOption[] = departments.map((dept) => ({
              value: dept,
              label: dept
            }));
            
            setLoadedOptions(mappedOptions);
            
          } else if (field.type === 'module-checkbox-group') {
            // Load modules
            response = await fetch('/api/v1/modules?status=active', { headers });
            if (!response.ok) {
              throw new Error(`Failed to load modules: ${response.statusText}`);
            }
            data = await response.json();
            
            let modulesArray: Array<{ module_key: string; module_name?: string; name?: string; description?: string }> =
              (Array.isArray(data)
                ? data
                : ((data as { modules?: unknown[]; data?: unknown[] }).modules
                  ?? (data as { data?: unknown[] }).data
                  ?? [])) as Array<{ module_key: string; module_name?: string; name?: string; description?: string }>;

            // Filter based on metadata
            if (field.metadata?.filter === 'user_billable') {
              // Exclude employee-based modules (for team member onboarding)
              const excludeModules = field.metadata?.exclude || [];
              modulesArray = modulesArray.filter((m) => !excludeModules.includes(m.module_key));
            } else if (field.metadata?.filter === 'employee_modules') {
              // Include only employee modules (for employee onboarding)
              const includeModules = field.metadata?.include || [];
              if (includeModules.length > 0) {
                modulesArray = modulesArray.filter((m) => includeModules.includes(m.module_key));
              }
            }

            const mappedOptions: FormFieldOption[] = modulesArray.map((module) => ({
              value: module.module_key,
              label: module.module_name || module.name || '',
              description: module.description
            }));
            
            setLoadedOptions(mappedOptions);
            
          } else if (field.endpoint) {
            // Standard endpoint loading
            response = await fetch(field.endpoint, { headers });
            
            if (!response.ok) {
              throw new Error(`Failed to load options: ${response.statusText}`);
            }
            
            data = await response.json();
            
            // Extract options from response
            let optionsArray: unknown = data;
            if (field.responseField && data && typeof data === 'object') {
              optionsArray = (data as Record<string, unknown>)[field.responseField];
            }

            if (!Array.isArray(optionsArray)) {
              throw new Error('Invalid options data received from endpoint');
            }

            // Map to FormFieldOption format
            const mappedOptions: FormFieldOption[] = (optionsArray as Record<string, unknown>[]).map((item) => ({
              value: String(field.valueField ? item[field.valueField] : item.value ?? ''),
              label: String(field.labelField ? item[field.labelField] : item.label ?? ''),
              description: typeof item.description === 'string' ? item.description : undefined
            }));
            
            setLoadedOptions(mappedOptions);
          }
        } catch (error) {
          console.error('[DynamicField] Error loading options:', error);
          setOptionsError(error instanceof Error ? error.message : 'Failed to load options');
        } finally {
          setLoadingOptions(false);
        }
      };
      
      loadOptions();
    }
  }, [field.endpoint, field.type, field.responseField, field.labelField, field.valueField, field.metadata, field.data_source, field.module_key]);
  
  // Normalize field.options — schemas store choices in different locations:
  // - field.options as array (legacy/inline)
  // - field.options.options (operational form schemas)
  // - field.options.choices (FormFieldEditor primary)
  // - field.options.items (FormFieldEditor secondary)
  const rawOptionList: unknown[] = Array.isArray(field.options)
    ? field.options
    : (field.options && typeof field.options === 'object')
      ? ([field.options.options, field.options.choices, field.options.items].find(Array.isArray) ?? [])
      : [];
  const normalizedOptions: FormFieldOption[] = rawOptionList.map((option) =>
    typeof option === 'string' || typeof option === 'number'
      ? { value: String(option), label: String(option) }
      : option as FormFieldOption,
  );
  // Use loaded options if available, otherwise use normalized field.options
  const fieldOptions = loadedOptions.length > 0 ? loadedOptions : normalizedOptions;
  
  const getInputClasses = (hasError = false) => {
    let baseClasses = `w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 transition-colors ${
      isDark 
        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400' 
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
    }`;
    
    if (hasError) {
      baseClasses += isDark 
        ? ' border-red-400 focus:ring-red-400 focus:border-red-400' 
        : ' border-red-300 focus:ring-red-500 focus:border-red-500';
    }
    
    if (field.styling?.classes) {
      baseClasses += ` ${field.styling.classes}`;
    }
    
    return baseClasses;
  };

  const renderField = () => {
    // Normalize to canonical field type so both legacy and canonical names work
    const fieldType = getCanonicalFieldType(field.type) as string;
    switch (fieldType) {
      case 'text':
      case 'email':
      case 'password':
      case 'phone':
        return (
          <input
            type={fieldType === 'phone' ? 'tel' : fieldType}
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={getInputClasses()}
            required={isRequired}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
          />
        );
      
      case 'url':
        // If enableImageUpload and field is for images (logo, hero), add upload button
        const isImageField = enableImageUpload && (
          field.key === 'logo_url' || 
          field.key.startsWith('hero') || 
          field.label?.toLowerCase().includes('logo') ||
          field.label?.toLowerCase().includes('hero') ||
          field.label?.toLowerCase().includes('image')
        );
        
        if (isImageField) {
          return <ImageUploadField field={field} value={value} onChange={onChange} isDark={isDark} />;
        }
        
        return (
          <input
            type="url"
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={getInputClasses()}
            required={isRequired}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
          />
        );

      case 'number':
        return (
          <input
            type="text"
            inputMode="decimal"
            pattern="-?[0-9]*\.?[0-9]*"
            value={asInputValue(value)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
                onChange(raw === '' ? null : raw);
                return;
              }
              // Keep as string while user is mid-edit (trailing dot or trailing decimal zeros)
              if (/^-?\d+\.$/.test(raw) || /^-?\d+\.\d*0+$/.test(raw)) {
                onChange(raw);
                return;
              }
              onChange(isNaN(Number(raw)) ? raw : Number(raw));
            }}
            placeholder={field.placeholder}
            className={getInputClasses()}
            style={{ touchAction: 'manipulation' }}
            autoComplete="off"
            required={isRequired}
          />
        );

      case 'currency':
        return (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {field.currency || 'ZAR'}
              </span>
            </div>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              value={asInputValue(value)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || raw === '.' ) {
                  onChange(raw === '' ? null : raw);
                  return;
                }
                // Keep as string while user is mid-edit (trailing dot or trailing decimal zeros)
                if (/^\d+\.$/.test(raw) || /^\d+\.\d*0+$/.test(raw)) {
                  onChange(raw);
                  return;
                }
                const num = parseFloat(raw);
                onChange(isNaN(num) ? raw : num);
              }}
              placeholder={field.placeholder || '0.00'}
              className={`${getInputClasses()} pl-16`}
              style={{ touchAction: 'manipulation' }}
              autoComplete="off"
              required={isRequired}
            />
          </div>
        );

      case 'textarea':
        // Use Google Places Autocomplete for address fields (if not readonly)
        if (!isFieldReadOnly && (field.key === 'physical_address' || field.key === 'postal_address' || field.key === 'address' || field.label?.toLowerCase().includes('address'))) {
          return (
            <GooglePlacesAutocomplete
              value={String(asInputValue(value))}
              onChange={onChange}
              placeholder={field.placeholder || 'Start typing to search for an address...'}
              disabled={isFieldReadOnly}
            />
          );
        }
        
        // Default textarea for non-address fields or readonly fields
        return (
          <textarea
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={getInputClasses()}
            required={isRequired}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
            rows={4}
          />
        );

      case 'select':
        if (loadingOptions) {
          return (
            <select className={getInputClasses()} disabled>
              <option>Loading options...</option>
            </select>
          );
        }
        if (optionsError) {
          return (
            <div className="text-sm text-red-600">
              Error loading options: {optionsError}
            </div>
          );
        }
        return (
          <select
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
            required={isRequired}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {fieldOptions.map((option: FormFieldOption, index: number) => (
              <option key={`option-${index}-${option.value ?? ''}`} value={option.value ?? ''}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
      case 'multiselect':
        if (loadingOptions) {
          return (
            <div className={`${getInputClasses()} flex items-center justify-center py-3`}>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading options...</span>
            </div>
          );
        }
        if (optionsError) {
          return (
            <div className="text-sm text-red-600">
              Error loading options: {optionsError}
            </div>
          );
        }
        return (
          <MultiSelect
            options={fieldOptions}
            value={Array.isArray(value) ? (value as (string | number)[]) : []}
            onChange={(selectedValues) => onChange(selectedValues)}
            placeholder={field.placeholder || `Select ${field.label || 'options'}...`}
            className="w-full"
            required={isRequired}
          />
        );

      case 'tags':
        return <TagsInputField field={field} value={value} onChange={onChange} isDark={isDark} />;

      case 'checkbox':
        return (
          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              required={isRequired}
            />
            <label className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {field.label}
            </label>
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {fieldOptions?.map((option, index: number) => (
              <div key={`radio-${index}-${option.value}`} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  required={isRequired}
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </div>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
            required={isRequired}
          />
        );

      case 'datetime':
      case 'datetime-local':
        // Format datetime for datetime-local input (yyyy-MM-ddTHH:mm)
        const formatDatetimeLocal = (val: FieldValue) => {
          if (typeof val !== 'string' && typeof val !== 'number') return '';
          try {
            const date = new Date(val);
            if (isNaN(date.getTime())) return '';
            // Format as yyyy-MM-ddTHH:mm (no seconds, no timezone)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
          } catch {
            return '';
          }
        };
        
        return (
          <input
            type="datetime-local"
            value={formatDatetimeLocal(value)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
            required={isRequired}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
            required={isRequired}
          />
        );

      case 'file_upload':
      case 'file':
        return <FileUploadField field={field} value={value} onChange={onChange} isDark={isDark} />;

      case 'color':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={typeof value === 'string' && value ? value : '#000000'}
              onChange={(e) => onChange(e.target.value)}
              className="h-10 w-20 rounded-md border cursor-pointer"
              required={isRequired}
            />
            <input
              type="text"
              value={asInputValue(value)}
              onChange={(e) => {
                // Validate hex color format
                const hex = e.target.value;
                if (/^#[0-9A-Fa-f]{6}$/.test(hex) || hex === '') {
                  onChange(hex);
                }
              }}
              placeholder="#000000"
              pattern="^#[0-9A-Fa-f]{6}$"
              className={getInputClasses()}
              required={isRequired}
              maxLength={7}
            />
          </div>
        );

      case 'hidden':
        return (
          <input
            type="hidden"
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'info':
        // Info field - just displays help text/information, not editable
        return (
          <div className={`rounded-lg p-4 ${
            isDark ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  {field.label && (
                    <div className="font-medium mb-1">{field.label}</div>
                  )}
                  {field.helpText && (
                    <div className="text-sm opacity-90">{field.helpText}</div>
                  )}
                  {field.placeholder && !field.helpText && (
                    <div className="text-sm opacity-90">{field.placeholder}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'custom':
        // Custom field - render based on componentType
        // Fallback for unknown custom component types
        return (
          <div className={`text-sm p-3 rounded ${isDark ? 'bg-yellow-900/20 text-yellow-200' : 'bg-yellow-50 text-yellow-700'}`}>
            Custom component type "{field.componentType}" needs to be implemented
          </div>
        );

      case 'dynamic_select':
      case 'dynamic-select':
        // For fields that load options dynamically (e.g., departments from client_details)
        if (loadingOptions) {
          return (
            <select className={getInputClasses()} disabled>
              <option>Loading options...</option>
            </select>
          );
        }
        if (optionsError) {
          return (
            <div className="text-sm text-red-600">
              Error loading options: {optionsError}
            </div>
          );
        }
        return (
          <select
            value={asInputValue(value)}
            onChange={(e) => onChange(e.target.value)}
            className={getInputClasses()}
            required={isRequired}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {fieldOptions.map((option: FormFieldOption, index: number) => (
              <option key={`option-${index}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'module-checkbox-group':
        // Special field type for module selection with role assignment
        if (loadingOptions) {
          return (
            <div className={`${getInputClasses()} flex items-center justify-center py-3`}>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading modules...</span>
            </div>
          );
        }
        if (optionsError) {
          return (
            <div className="text-sm text-red-600">
              Error loading modules: {optionsError}
            </div>
          );
        }
        
        // value is an array of {module_key, role, ...conditionalData}
        const selectedModules = asArray<string | ModuleRoleAssignment>(value);
        const autoGrantModules = field.metadata?.autoGrantModules || [];
        
        return (
          <div className="space-y-3">
            {/* Show auto-grant message if applicable */}
            {autoGrantModules.length > 0 && field.metadata?.autoGrantMessage && (
              <div className={`text-xs p-2 rounded border ${
                isDark 
                  ? 'bg-blue-900/20 border-blue-700 text-blue-300' 
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                ℹ️ {field.metadata.autoGrantMessage}
              </div>
            )}
            
            {fieldOptions.map((module: FormFieldOption) => {
              const isAutoGrant = autoGrantModules.includes(module.value);
              const isSelected = selectedModules.some((m) =>
                typeof m === 'string' ? m === module.value : m.module_key === module.value
              );
              const currentModule = selectedModules.find((m) =>
                typeof m === 'string' ? m === module.value : m.module_key === module.value
              );
              const currentRole = typeof currentModule === 'object' ? currentModule.role : 
                (field.metadata?.defaultRole || 'user');
              
              // Check if this module has conditional fields
              const conditionalFields = field.metadata?.conditionalFields?.[module.value] || [];
              
              return (
                <div 
                  key={module.value}
                  className={`border rounded-lg p-3 ${
                    isDark 
                      ? isSelected || isAutoGrant ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 bg-gray-700'
                      : isSelected || isAutoGrant ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {isAutoGrant ? (
                      <div className={`h-4 w-4 mt-1 flex items-center justify-center text-blue-600`}>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    ) : (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Add module with default role
                            onChange([...selectedModules, { 
                              module_key: module.value, 
                              role: field.metadata?.defaultRole || 'user' 
                            }]);
                          } else {
                            // Remove module
                            onChange(selectedModules.filter((m) =>
                              typeof m === 'string' ? m !== module.value : m.module_key !== module.value
                            ));
                          }
                        }}
                        className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {module.label}
                          {isAutoGrant && (
                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                              isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'
                            }`}>
                              Auto-granted
                            </span>
                          )}
                        </label>
                        {module.description && (
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {module.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Role selector - show for selected modules OR auto-grant modules */}
                      {(isSelected || isAutoGrant) && field.metadata?.showRoleSelector && (
                        <div className="mt-2">
                          <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Permission Level {isAutoGrant && '(configure role)'}
                          </label>
                          <select
                            value={currentRole}
                            onChange={(e) => {
                              if (isAutoGrant) {
                                // For auto-grant modules, always ensure they're in the array
                                const hasModule = selectedModules.some((m) =>
                                  typeof m === 'string' ? m === module.value : m.module_key === module.value
                                );
                                
                                if (hasModule) {
                                  // Update existing entry
                                  const newModules = selectedModules.map((m): string | ModuleRoleAssignment => {
                                    if (typeof m === 'string') {
                                      return { module_key: module.value ?? '', role: e.target.value };
                                    } else {
                                      return m.module_key === module.value
                                        ? { ...m, role: e.target.value }
                                        : m;
                                    }
                                  });
                                  onChange(newModules);
                                } else {
                                  // Add new entry with selected role
                                  onChange([...selectedModules, {
                                    module_key: module.value ?? '',
                                    role: e.target.value
                                  }]);
                                }
                              } else {
                                // For non-auto-grant modules, just update role
                                const newModules = selectedModules.map((m): string | ModuleRoleAssignment => {
                                  if (typeof m === 'string') {
                                    return m === module.value 
                                      ? { module_key: module.value, role: e.target.value }
                                      : { module_key: m, role: field.metadata?.defaultRole || 'user' };
                                  } else {
                                    return m.module_key === module.value
                                      ? { ...m, role: e.target.value }
                                      : m;
                                  }
                                });
                                onChange(newModules);
                              }
                            }}
                            className={`mt-1 text-sm px-2 py-1.5 rounded border w-full ${
                              isDark
                                ? 'bg-gray-600 border-gray-500 text-gray-200'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            {field.metadata?.allowedRoles?.map((role: string) => (
                              <option key={role} value={role}>
                                {role.charAt(0).toUpperCase() + role.slice(1)}
                              </option>
                            )) || (
                              <>
                                <option value="user">User</option>
                                <option value="supervisor">Supervisor</option>
                                <option value="admin">Admin</option>
                              </>
                            )}
                          </select>
                        </div>
                      )}
                      
                      {/* Conditional fields - show when module is selected or auto-granted */}
                      {(isSelected || isAutoGrant) && conditionalFields.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600 space-y-2">
                          {conditionalFields.map((condField) => {
                            const condFieldValue =
                              currentModule && typeof currentModule === 'object'
                                ? (currentModule[condField.key] as FieldValue)
                                : undefined;
                            
                            return (
                              <DynamicConditionalField
                                key={condField.key}
                                field={condField}
                                value={condFieldValue}
                                onChange={(newValue) => {
                                  const newModules = selectedModules.map((m) => {
                                    if (typeof m === 'object' && m.module_key === module.value) {
                                      return { ...m, [condField.key]: newValue };
                                    }
                                    return m;
                                  });
                                  onChange(newModules);
                                }}
                                isDark={isDark}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {fieldOptions.length === 0 && (
              <div className={`text-sm text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No modules available
              </div>
            )}
          </div>
        );

      case 'module-role-config':
        // New field type: Shows auto-granted modules with role selector and required fields
        // No checkboxes - modules are always active
        const modules = field.metadata?.modules || [];
        const moduleRoles = asArray<ModuleRoleAssignment>(value);
        
        return (
          <div className="space-y-3">
            {/* Info message */}
            {field.metadata?.autoGrantMessage && (
              <div className={`text-xs p-2.5 rounded border ${
                isDark 
                  ? 'bg-blue-900/20 border-blue-700 text-blue-300' 
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                ℹ️ {field.metadata.autoGrantMessage}
              </div>
            )}
            
            {modules.map((module) => {
              const currentModule = moduleRoles.find((m) => m.module_key === module.key) || {
                module_key: module.key,
                role: field.metadata?.defaultRole || 'employee'
              };
              
              return (
                <div 
                  key={module.key}
                  className={`border rounded-lg p-4 ${
                    isDark 
                      ? 'border-blue-500 bg-blue-900/10' 
                      : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="space-y-3">
                    {/* Module header */}
                    <div className="flex items-start gap-2">
                      <div className="h-5 w-5 mt-0.5 flex items-center justify-center text-blue-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <label className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {module.label}
                          </label>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800'
                          }`}>
                            Auto-granted
                          </span>
                        </div>
                        {module.description && (
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {module.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Role selector */}
                    {field.metadata?.showRoleSelector && (
                      <div>
                        <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Permission Level *
                        </label>
                        <select
                          value={currentModule.role}
                          onChange={(e) => {
                            const newRoles = moduleRoles.some((m) => m.module_key === module.key)
                              ? moduleRoles.map((m) => 
                                  m.module_key === module.key 
                                    ? { ...m, role: e.target.value }
                                    : m
                                )
                              : [...moduleRoles, { module_key: module.key, role: e.target.value }];
                            onChange(newRoles);
                          }}
                          className={`mt-1 text-sm px-3 py-2 rounded border w-full ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-gray-200'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          {field.metadata?.allowedRoles?.map((role: string) => (
                            <option key={role} value={role}>
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </option>
                          ))}
                        </select>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Defines what actions the employee can perform in this module
                        </p>
                      </div>
                    )}
                    
                    {/* Required fields for this module */}
                    {module.requiredFields && module.requiredFields.length > 0 && (
                      <div className="pt-3 border-t border-gray-300 dark:border-gray-600 space-y-3">
                        {module.requiredFields.map((reqField) => {
                          const fieldValue = currentModule[reqField.key] as FieldValue;
                          
                          return (
                            <div key={reqField.key}>
                              <DynamicConditionalField
                                field={reqField}
                                value={fieldValue}
                                onChange={(newValue) => {
                                  const newRoles = moduleRoles.some((m) => m.module_key === module.key)
                                    ? moduleRoles.map((m) => 
                                        m.module_key === module.key 
                                          ? { ...m, [reqField.key]: newValue }
                                          : m
                                      )
                                    : [...moduleRoles, { 
                                        module_key: module.key, 
                                        role: field.metadata?.defaultRole || 'employee',
                                        [reqField.key]: newValue 
                                      }];
                                  onChange(newRoles);
                                }}
                                isDark={isDark}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'contact_picker':
        return (
          <ContactPicker
            value={typeof value === 'number' ? value : null}
            onChange={(contactId, contact) => {
              onChange(contactId);
              // If the field has callbacks to update related fields, trigger them
              if (field.metadata?.updateFields) {
                field.metadata.updateFields(contact);
              }
            }}
            placeholder={field.placeholder}
            isDisabled={isFieldReadOnly}
          />
        );

      case 'location_picker':
        // If depends on contact, get contact_id from fullFormData
        const locationPickerOptions = !Array.isArray(field.options) && field.options ? field.options : undefined;
        const contactIdForLocation = locationPickerOptions?.depends_on_contact && fullFormData
          ? (typeof fullFormData.contact_id === 'number' ? fullFormData.contact_id : null)
          : null;
        
        console.log('[DynamicField] location_picker - fullFormData:', fullFormData, 'contactId:', contactIdForLocation);
        
        return (
          <LocationPicker
            key={`location-picker-${contactIdForLocation || 'none'}`}
            value={typeof value === 'number' ? value : null}
            onChange={(locationId, location) => {
              onChange(locationId);
              // If the field has callbacks to update related fields, trigger them
              if (field.metadata?.updateFields) {
                field.metadata.updateFields(location);
              }
            }}
            contactId={contactIdForLocation}
            placeholder={field.placeholder}
            isDisabled={isFieldReadOnly}
          />
        );

      case 'location_search':
        // Search ALL locations, auto-populate contact when selected
        // The onChange here will receive an object with multiple field updates
        return (
          <LocationSearchPicker
            value={typeof value === 'number' ? value : null}
            onChange={(locationId, location) => {
              // Update the location_id field
              onChange(locationId);
              
              // If location has contact_id and autoPopulateContact is enabled,
              // we need to also update contact_id. We do this by calling onChange
              // with a special format that DynamicFormBuilder can recognize.
              if (field.metadata?.autoPopulateContact && location?.contact_id) {
                // Call the metadata callback to update contact_id
                if (field.metadata?.onLocationSelected) {
                  field.metadata.onLocationSelected(location);
                }
              }
              
              // If the field has callbacks to update related fields, trigger them
              if (field.metadata?.updateFields) {
                field.metadata.updateFields(location);
              }
            }}
            onContactChange={(contactId) => {
              // This is called when location is selected - update contact_id field
              // We need to trigger a form update for contact_id
              console.log('[DynamicField] location_search - need to update contact_id:', contactId);
            }}
            placeholder={field.placeholder}
            disabled={isFieldReadOnly}
          />
        );

      case 'user_multi_select':
        // Convert value (array of user IDs) to proper format
        const userIds = Array.isArray(value) ? (value as number[]) : (value ? [value as number] : []);
        
        // Log the full field to debug options structure
        console.log('[DynamicField] user_multi_select - FULL FIELD:', JSON.stringify(field, null, 2));
        
        // Extract module_key from options (object format) or metadata
        const userSelectOptions = !Array.isArray(field.options) && field.options ? field.options : undefined;
        const moduleKey = userSelectOptions?.module_key || field.metadata?.module_key || field.module_key;
        
        console.log('[DynamicField] user_multi_select - fieldOptions:', userSelectOptions, 'moduleKey:', moduleKey);
        
        return (
          <UserMultiSelect
            value={userIds}
            onChange={(selectedUserIds) => onChange(selectedUserIds)}
            placeholder={field.placeholder}
            isDisabled={isFieldReadOnly}
            moduleKey={moduleKey}
          />
        );

      case 'image':
        // Single image upload with preview (for primary product image)
        return (
          <ImageUploadField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'product_images':
        // Enterprise product image gallery
        return (
          <ProductImagesField
            workItemId={workItemIdString(fullFormData?.id ?? field.metadata?.workItemId)}
            value={Array.isArray(value) ? (value as ProductImage[]) : []}
            onChange={(images) => onChange(images)}
            maxImages={field.metadata?.maxImages || 10}
            disabled={isFieldReadOnly}
            showPrimarySelector={field.metadata?.showPrimarySelector !== false}
            showCaptions={field.metadata?.showCaptions !== false}
            showAltText={field.metadata?.showAltText || false}
            acceptedTypes={field.metadata?.acceptedTypes || 'image/jpeg,image/png,image/webp,image/gif'}
            maxSizeMB={field.metadata?.maxSizeMB || 10}
            label={field.label}
            helpText={field.help_text}
            isDark={isDark}
          />
        );

      case 'measurement':
        return (
          <MeasurementField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'pass_fail':
        return (
          <PassFailField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'photo_capture':
        return (
          <PhotoCaptureField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'signature':
      case 'signature_pad':
        return (
          <SignaturePadField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
            workItemId={workItemIdString(fullFormData?.id ?? field.metadata?.workItemId)}
          />
        );

      case 'gps_stamp':
        return (
          <GpsStampField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'barcode_scanner':
        return (
          <BarcodeScannerField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'comments':
      case 'comment':
        return (
          <CommentsField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'otp':
        return (
          <OtpVerificationField
            field={field}
            value={value}
            onChange={onChange}
            isDark={isDark}
          />
        );

      case 'rating':
        return <RatingField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={isFieldReadOnly} />;

      case 'scale':
        return <LinearScaleField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={isFieldReadOnly} />;

      case 'matrix':
        return <MatrixGridField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={isFieldReadOnly} />;

      case 'ranking':
        return <RankingField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={isFieldReadOnly} />;

      case 'divider':
        return <DividerField field={field} isDark={isDark} />;

      case 'heading':
        return <HeadingField field={field} isDark={isDark} />;

      case 'image_display':
        return <ImageDisplayField field={field} isDark={isDark} />;

      default:
        return (
          <div className="text-sm text-red-600">
            Unsupported field type: {field.type}
          </div>
        );
    }
  };

  // Don't render label for hidden or layout-only fields
  if (field.type === 'hidden' || field.type === 'divider' || field.type === 'heading' || field.type === 'image_display') {
    return renderField();
  }

  // ── Read-only static display ──────────────────────────────────
  if (isFieldReadOnly) {
    const formatReadOnlyValue = (): string => {
      if (value === null || value === undefined || value === '') return '—';

      // Normalize field type for read-only formatting
      const roType = getCanonicalFieldType(field.type);

      // Boolean / checkbox / toggle
      if (roType === 'checkbox' || field.type === 'toggle' || typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Select / radio — resolve label from options
      if (roType === 'select' || roType === 'radio' || roType === 'dynamic_select') {
        const opt = fieldOptions.find((o) => String(o.value) === String(value));
        return opt?.label || String(value);
      }

      // Multi-select
      if (roType === 'multi_select' || field.type === 'multi-select' || roType === 'tags') {
        const arr = Array.isArray(value) ? value : [value];
        return arr.map((v) => {
          const opt = fieldOptions.find((o) => String(o.value) === String(v));
          return opt?.label || String(v);
        }).join(', ') || '—';
      }

      // Date / datetime
      if (roType === 'date' || field.type === 'datetime-local' || roType === 'datetime') {
        try {
          const d = new Date(value as string | number);
          return roType === 'date' ? d.toLocaleDateString() : d.toLocaleString();
        } catch { return String(value); }
      }

      // Currency
      if (roType === 'currency' || field.type === 'money') {
        const num = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(num)) return String(value);
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: field.currency || 'ZAR' }).format(num);
      }

      // Number
      if (roType === 'number') {
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      }

      // Textarea / rich text — show as-is
      if (roType === 'textarea' || field.type === 'rich_text') {
        return String(value);
      }

      // Pass/fail — value may be a string ('pass'/'fail'/'na') or an object { result, comment }
      if (roType === 'pass_fail') {
        const result = typeof value === 'object' && value !== null
          ? (value as Record<string, unknown>).result
          : value;
        return result === 'pass' ? '✅ Pass' : result === 'fail' ? '❌ Fail' : result === 'na' ? 'N/A' : String(result);
      }

      return String(value);
    };

    // Special handling for photo / signature / image fields — show thumbnails
    if (['photo_capture', 'signature_pad', 'product_images', 'image'].includes(field.type) && value) {
      const images = Array.isArray(value) ? value : [value];
      const validImages = images
        .map((img) => {
          if (typeof img === 'string' && (img.startsWith('http') || img.startsWith('data:image'))) return img;
          if (img && typeof img === 'object' && !Array.isArray(img)) {
            const rec = img as { data_url?: unknown; url?: unknown };
            if (rec.data_url || rec.url) return String(rec.data_url ?? rec.url);
          }
          return null;
        })
        .filter(Boolean) as string[];
      if (validImages.length > 0) {
        return (
          <div
            className={`space-y-2 ${field.styling?.containerClasses || ''}`}
            style={fieldStyles.container}
          >
            <label
              className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
              style={fieldStyles.label}
            >
              {field.label}
            </label>
            <div className="flex flex-wrap gap-2">
              {validImages.map((url: string, i: number) => (
                <img key={i} src={url} alt={`${field.label} ${i + 1}`} className="w-16 h-16 rounded-md object-cover border border-gray-300 dark:border-gray-600" />
              ))}
            </div>
          </div>
        );
      }
    }

    const displayValue = formatReadOnlyValue();
    const isLongText = (field.type === 'textarea' || field.type === 'rich_text') && displayValue.length > 80;

    return (
      <div
        className={`space-y-1 ${field.styling?.containerClasses || ''}`}
        style={fieldStyles.container}
      >
        <label
          className={`block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          style={fieldStyles.label}
        >
          {field.label}
        </label>
        <div className={`text-sm ${
          displayValue === '—'
            ? (isDark ? 'text-gray-500 italic' : 'text-gray-400 italic')
            : (isDark ? 'text-gray-200' : 'text-gray-900')
        } ${isLongText ? 'whitespace-pre-wrap' : ''}`}>
          {displayValue}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`space-y-2 ${field.styling?.containerClasses || ''}`}
      style={fieldStyles.container}
    >
      <label
        htmlFor={field.key}
        className={`flex items-center gap-2 text-sm font-medium ${
          isDark ? 'text-gray-200' : 'text-gray-700'
        }`}
        style={fieldStyles.label}
      >
        {fieldNumber !== undefined && (
          <span
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold flex-shrink-0 ${
              isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {fieldNumber}
          </span>
        )}
        {field.label}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {renderField()}

      {field.validation?.message && (
        <p className={`text-xs ${
          isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>{field.validation.message}</p>
      )}

      {validationError && (
        <p className={`text-xs text-red-500 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
          {validationError}
        </p>
      )}
    </div>
  );
};

export default DynamicField;